import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';

const CreateProjectModal = ({ isOpen, onClose, onProjectCreated, title, description }) => {
  const { jeweller } = useAuth();
  const jewellerId = jeweller?.id;
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Step 1: Customer Selection
  const [isExistingCustomer, setIsExistingCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '' });
  // Step 2: Project Stage
  const [selectedStatus, setSelectedStatus] = useState('');
  // Step 3: Project Details
  const [projectDetails, setProjectDetails] = useState({
    projectName: '',
    description: '',
    budget: '',
    timeline: '',
    notes: ''
  });
  const statusOptions = [
    { value: 'new_enquiry', label: 'New Enquiry', description: 'Customer just made initial contact' },
    { value: 'in_conversation', label: 'In Conversation', description: 'Discussing requirements and details' },
    { value: 'project_started', label: 'Active Project', description: 'Work has begun, ready for invoicing' },
    { value: 'project_completed', label: 'Project Completed', description: 'Work is finished' }
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsExistingCustomer(null);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedCustomer(null);
      setNewCustomer({ name: '', email: '' });
      setSelectedStatus('');
      setProjectDetails({
        projectName: '',
        description: '',
        budget: '',
        timeline: '',
        notes: ''
      });
    }
  }, [isOpen]);

  // Search existing customers
  useEffect(() => {
    if (!searchQuery.trim() || !jewellerId) {
      setSearchResults([]);
      return;
    }

    let isCancelled = false;

    const searchCustomers = async () => {
      try {
        // Get this jeweller's projects
        const projectsQueryRef = query(
          collection(db, 'projects'),
          where('jewellerId', '==', jewellerId)
        );
        const projectsSnapshot = await getDocs(projectsQueryRef);

        // Build an index of customers from project docs
        const seen = new Map();
        projectsSnapshot.docs.forEach(docSnap => {
          const d = docSnap.data();
          const key = d.userId || (d.customerInfo?.email || '').toLowerCase();
          if (!key) return;
          if (!seen.has(key)) {
            seen.set(key, {
              id: d.userId || null,
              name: d.customerInfo?.name || '',
              email: d.customerInfo?.email || '',
            });
          }
        });

        const q = searchQuery.toLowerCase();
        const results = Array.from(seen.values()).filter(c =>
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.email && c.email.toLowerCase().includes(q))
        );
        
        if (!isCancelled) {
            setSearchResults(results);
        }
      } catch (error) {
        console.error('Error searching customers:', error);
        if (!isCancelled) {
          setSearchResults([]);
        }
      }
    };

    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => {
      isCancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [searchQuery, jewellerId]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let customerInfo;
      let userId;
      // Handle customer creation/selection
      if (isExistingCustomer) {
        customerInfo = {
          name: selectedCustomer.name,
          email: selectedCustomer.email
        };
        userId = selectedCustomer.id;
        console.log('Using existing customer:', userId, customerInfo);
      } else {
        // Create new customer
        console.log('Creating new customer:', newCustomer);
        customerInfo = { name: newCustomer.name, email: newCustomer.email };
        userId = null; // you don’t know their UID yet (they haven't signed up)
        console.log('Created new customer with ID:', userId);
      }

      // Create project with status history
      const now = new Date();
      const projectData = {
        customerId: userId,
        newCustomer: !isExistingCustomer ? customerInfo : undefined,
        projectName: projectDetails.projectName,
        description: projectDetails.description,
        status: selectedStatus,
        enquiryDetails: {
          budget: projectDetails.budget,
          timeline: projectDetails.timeline,
          message: `Manual project creation: ${projectDetails.description}`
        },
        notes: projectDetails.notes
      };
      console.log('Creating project via callable with data:', projectData);

      const createProject = httpsCallable(functions, 'createProject');
      const { data } = await createProject(projectData);

      const newProject = {
        id: data?.projectId || data?.id,
        ...data,
        status: data?.status || selectedStatus,
        notes: data?.notes ?? projectDetails.notes,
        enquiryDetails: data?.enquiryDetails || projectData.enquiryDetails
      };
      console.log('Project created successfully with data:', newProject);
      
      onProjectCreated(newProject);
      onClose();
    } catch (error) {
      console.error('Error creating project:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      alert(`Failed to create project: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = () => {
    if (isExistingCustomer === null) return false;
    if (isExistingCustomer && !selectedCustomer) return false;
    if (!isExistingCustomer && (!newCustomer.name.trim() || !newCustomer.email.trim())) return false;
    return true;
  };

  const canProceedStep2 = () => selectedStatus !== '';

  const canSubmit = () => {
    return projectDetails.projectName.trim() && projectDetails.description.trim();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {title || 'Create New Project'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Step {currentStep} of 3
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Progress Bar */}
        <div className="px-6 py-2">
          <div className="flex items-center">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        {/* Step Content */}
        <div className="p-6">
          {/* Step 1: Customer Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Who is this project for?
                </h3>
                
                <div className="space-y-4">
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="customerType"
                      value="existing"
                      checked={isExistingCustomer === true}
                      onChange={() => setIsExistingCustomer(true)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Existing Customer</div>
                      <div className="text-sm text-gray-600">Someone you've worked with through Boutee before</div>
                    </div>
                  </label>
                  <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="customerType"
                      value="new"
                      checked={isExistingCustomer === false}
                      onChange={() => setIsExistingCustomer(false)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">New Customer</div>
                      <div className="text-sm text-gray-600">Someone new who hasn't used Boutee before</div>
                    </div>
                  </label>
                </div>
              </div>
              {/* Existing Customer Search */}
              {isExistingCustomer === true && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search for customer
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Type customer name..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {searchResults.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
                      {searchResults.map((customer) => (
                        <button
                          key={customer.id}
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setSearchQuery(customer.name);
                            setSearchResults([]);
                          }}
                          className="w-full text-left p-3 hover:bg-blue-50 border-b last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{customer.name}</div>
                          <div className="text-sm text-gray-600">{customer.email}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedCustomer && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="font-medium text-blue-900">Selected: {selectedCustomer.name}</div>
                      <div className="text-sm text-blue-700">{selectedCustomer.email}</div>
                    </div>
                  )}
                </div>
              )}
              {/* New Customer Form */}
              {isExistingCustomer === false && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Full name"
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="customer@example.com"
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Step 2: Project Stage */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  What stage is this project at?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  This helps us set up the right tools and workflow for your project.
                </p>
                
                <div className="space-y-3">
                  {statusOptions.map((option) => (
                    <label key={option.value} className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="projectStage"
                        value={option.value}
                        checked={selectedStatus === option.value}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-600">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* Step 3: Project Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Project Details
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Add the key information about this project.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={projectDetails.projectName}
                    onChange={(e) => setProjectDetails(prev => ({ ...prev, projectName: e.target.value }))}
                    placeholder="e.g., Custom Engagement Ring"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={projectDetails.description}
                    onChange={(e) => setProjectDetails(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe the project details, materials, specifications..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget
                    </label>
                    <input
                      type="text"
                      value={projectDetails.budget}
                      onChange={(e) => setProjectDetails(prev => ({ ...prev, budget: e.target.value }))}
                      placeholder="e.g., £5,000"
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timeline
                    </label>
                    <input
                      type="text"
                      value={projectDetails.timeline}
                      onChange={(e) => setProjectDetails(prev => ({ ...prev, timeline: e.target.value }))}
                      placeholder="e.g., 6 weeks"
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={projectDetails.notes}
                    onChange={(e) => setProjectDetails(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes about the customer, requirements, or project..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {currentStep < 3 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  (currentStep === 1 && !canProceedStep1()) ||
                  (currentStep === 2 && !canProceedStep2())
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit() || isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Project'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
